import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material'
import { ReactElement, useEffect, useState } from 'react'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import axios from 'src/utilities/http-client'
import {
  fetchSeries,
  fetchCourses,
  fetchClasses,
  fetchVideos,
  selectSeries,
  selectSeriesStatus,
  selectCourses,
  selectClasses,
  selectVideos,
} from 'src/v3/create/create.slice'
import { AddIcon, DeleteIcon, VideoIcon, SaveIcon } from 'src/v3/core/icons'
import { HeroSlide } from 'src/v3/core/types'
import DragHandleIcon from '@mui/icons-material/DragHandle'

type VideoOption = {
  id: string
  name: string
  fileName: string
  classId: string
  className: string
  courseId: string
  courseName: string
}

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let series = useAppSelector(selectSeries)
  let seriesStatus = useAppSelector(selectSeriesStatus)

  let [selectedSeriesId, setSelectedSeriesId] = useState<string>('')
  let [slides, setSlides] = useState<HeroSlide[]>([])
  let [availableVideos, setAvailableVideos] = useState<VideoOption[]>([])
  let [isLoading, setIsLoading] = useState(false)
  let [saving, setSaving] = useState(false)
  let [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'info'
  }>({ open: false, message: '', severity: 'success' })
  let [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    title: string
    message: string
    action: () => void
  }>({ open: false, title: '', message: '', action: () => {} })

  // Load existing slides from API
  let loadSlides = async () => {
    try {
      setIsLoading(true)
      let response = await axios.get('/api/create/slide')
      let s = response.data || []

      // Clean up videoSrc - extract filename from full URL
      let cleanedSlides = s.map((slide: HeroSlide) => ({
        ...slide,
        videoSrc: slide.videoSrc?.includes('/')
          ? slide.videoSrc.substring(slide.videoSrc.lastIndexOf('/') + 1)
          : slide.videoSrc,
      }))

      setSlides(cleanedSlides)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading slides:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Load initial data
  useEffect(() => {
    if (seriesStatus === 'uninitialized') {
      dispatch(fetchSeries())
    }
    loadSlides()
  }, [dispatch, seriesStatus])

  // Get course, class, and video data
  let courses = useAppSelector(selectCourses)
  let classes = useAppSelector(selectClasses)
  let videos = useAppSelector(selectVideos)

  // Load courses, classes, and videos on mount
  useEffect(() => {
    dispatch(fetchCourses())
    dispatch(fetchClasses())
    dispatch(fetchVideos())
  }, [dispatch])

  // Filter videos for selected series
  useEffect(() => {
    if (!selectedSeriesId || !courses || !classes || !videos || !series) {
      setAvailableVideos([])
      return
    }

    let selectedSeries = series.find((s) => s.id === selectedSeriesId)
    if (!selectedSeries) {
      setAvailableVideos([])
      return
    }

    setIsLoading(true)

    // Get course IDs that belong to the selected series
    let seriesCourseIDs = selectedSeries.courses?.map((sc) => sc.courseID) || []

    // Filter classes to only those in courses that belong to the series
    let seriesClassIDs = classes
      .filter((classItem) => seriesCourseIDs.includes(classItem.courseID))
      .map((classItem) => classItem.id)

    // Filter videos to only those in classes within the series
    let videoOptions = videos
      .filter((video) => seriesClassIDs.includes(video.classID))
      .map((video) => {
        let classItem = classes.find((c) => c.id === video.classID)
        let course = courses.find((c) => c.id === classItem?.courseID)

        return {
          id: video.id,
          name: video.name,
          fileName: video.fileName,
          classId: video.classID,
          className: classItem?.name || 'Unknown Class',
          courseId: course?.id || '',
          courseName: course?.name || course?.displayName || 'Unknown Course',
        }
      })

    setAvailableVideos(videoOptions)
    setIsLoading(false)
  }, [selectedSeriesId, series, courses, classes, videos])

  let handleAddSlide = async (video: VideoOption) => {
    let selectedSeries = series?.find((s) => s.id === selectedSeriesId)

    // Extract just the filename from video.fileName (in case it contains full path)
    let filename = video.fileName.includes('/')
      ? video.fileName.substring(video.fileName.lastIndexOf('/') + 1)
      : video.fileName

    let newSlideData = {
      title: selectedSeries?.name || '',
      description: selectedSeries?.description || '',
      imageName: selectedSeries?.thumbnail || '',
      videoSrc: filename.replace('.mp4', '.m3u8'),
      startTime: 0,
      endTime: 30,
      seriesID: selectedSeriesId,
      buttonText: 'Go to Series',
      sequence: slides.length,
      published: false,
    }

    try {
      setSaving(true)
      let response = await axios.post('/api/create/slide', newSlideData)
      setSlides([...slides, response.data])
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating slide:', error)
      setSnackbar({
        open: true,
        message: 'Error creating slide',
        severity: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  let handleRemoveSlide = (index: number) => {
    let slide = slides[index]
    if (!slide?.id) return

    setConfirmDialog({
      open: true,
      title: 'Delete Slide',
      message: `Are you sure you want to delete "${slide.title}"? This action cannot be undone.`,
      action: async () => {
        try {
          setSaving(true)
          await axios.delete(`/api/create/slide/${slide.id}`)
          setSlides(slides.filter((_, i) => i !== index))
          setSnackbar({
            open: true,
            message: 'Slide deleted successfully',
            severity: 'success',
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error deleting slide:', error)
          setSnackbar({
            open: true,
            message: 'Error deleting slide',
            severity: 'error',
          })
        } finally {
          setSaving(false)
        }
      },
    })
  }

  let handleSlideUpdate = async (
    index: number,
    field: keyof HeroSlide,
    value: any,
  ) => {
    let slide = slides[index]
    if (!slide?.id) return

    let updatedSlide = { ...slide, [field]: value }

    // Update local state immediately for better UX
    let updatedSlides = slides.map((s, i) => (i === index ? updatedSlide : s))
    setSlides(updatedSlides)

    // Debounce API call - save after user stops typing
    try {
      await axios.put(`/api/create/slide/${slide.id}`, {
        title: updatedSlide.title,
        description: updatedSlide.description,
        imageName: updatedSlide.imageName,
        videoSrc: updatedSlide.videoSrc,
        startTime: updatedSlide.startTime,
        endTime: updatedSlide.endTime,
        seriesID: updatedSlide.seriesID,
        buttonText: updatedSlide.buttonText,
        sequence: updatedSlide.sequence,
        published: updatedSlide.published,
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating slide:', error)
      // Revert local state on error
      setSlides(slides)
    }
  }

  let handleDragEnd = async (result: any) => {
    if (!result.destination) return

    let sourceIndex = result.source.index
    let destinationIndex = result.destination.index

    // Remove source item and insert at destination using functional approach
    let slideToMove = slides[sourceIndex]
    let slidesWithoutSource = slides.filter((_, index) => index !== sourceIndex)

    let reorderedSlides = [
      ...slidesWithoutSource.slice(0, destinationIndex),
      slideToMove,
      ...slidesWithoutSource.slice(destinationIndex),
    ]

    // Update sequence numbers
    let updatedSlides = reorderedSlides.map((slide, index) => ({
      ...slide,
      sequence: index,
    }))

    setSlides(updatedSlides)

    // Send reorder request to API
    try {
      let reorderData = {
        slides: updatedSlides.map((slide) => ({
          id: slide.id,
          sequence: slide.sequence,
        })),
      }
      await axios.patch('/api/create/slide/reorder', reorderData)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error reordering slides:', error)
      // Revert on error
      setSlides(slides)
    }
  }

  let handlePublishAll = () => {
    let unpublishedCount = slides.filter((slide) => !slide.published).length

    if (unpublishedCount === 0) {
      setSnackbar({
        open: true,
        message: 'All slides are already published',
        severity: 'info',
      })
      return
    }

    setConfirmDialog({
      open: true,
      title: 'Publish All Slides',
      message: `Are you sure you want to publish ${unpublishedCount} slide${unpublishedCount > 1 ? 's' : ''}? They will be visible to all users.`,
      action: async () => {
        try {
          setSaving(true)

          // Update all slides to published status
          let publishPromises = slides.map((slide) =>
            axios.put(`/api/create/slide/${slide.id}`, {
              title: slide.title,
              description: slide.description,
              imageName: slide.imageName,
              videoSrc: slide.videoSrc,
              startTime: slide.startTime,
              endTime: slide.endTime,
              seriesID: slide.seriesID,
              buttonText: slide.buttonText,
              sequence: slide.sequence,
              published: true,
            }),
          )

          await Promise.all(publishPromises)

          // Update local state
          let updatedSlides = slides.map((slide) => ({
            ...slide,
            published: true,
          }))
          setSlides(updatedSlides)

          setSnackbar({
            open: true,
            message: 'All slides published successfully!',
            severity: 'success',
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error publishing slides:', error)
          setSnackbar({
            open: true,
            message: 'Error publishing slides',
            severity: 'error',
          })
        } finally {
          setSaving(false)
        }
      },
    })
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>
          Marketing Slideshow Manager
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Create and manage hero slideshow content for the self-paced learning
          page.
        </Typography>
      </Box>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Left Panel - Series Selection & Video Library */}
        <div>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h4" sx={{ mb: 2 }}>
                Select Series
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Choose a series</InputLabel>
                <Select
                  value={selectedSeriesId}
                  onChange={(e) => setSelectedSeriesId(e.target.value)}
                  label="Choose a series"
                >
                  {series?.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>

          {selectedSeriesId && (
            <Card>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Available Videos
                </Typography>
                {isLoading ? (
                  <Typography>Loading videos...</Typography>
                ) : (
                  <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                    {availableVideos.length === 0 ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: 'center', py: 4 }}
                      >
                        {selectedSeriesId
                          ? 'No videos found in this series'
                          : 'Select a series to see available videos'}
                      </Typography>
                    ) : (
                      availableVideos.map((video) => (
                        <Paper
                          key={video.id}
                          sx={{
                            p: 2,
                            mb: 2,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                          onClick={() => handleAddSlide(video)}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                            }}
                          >
                            <VideoIcon color="primary" />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2">
                                {video.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {video.courseName} → {video.className}
                              </Typography>
                            </Box>
                            <IconButton size="small">
                              <AddIcon />
                            </IconButton>
                          </Box>
                        </Paper>
                      ))
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Panel - Slideshow Builder */}
        <div>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h4">
                  Slideshow ({slides.length} slides)
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handlePublishAll}
                  disabled={slides.length === 0 || saving}
                >
                  Publish All Slides
                </Button>
              </Box>

              {slides.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    Select a series and add videos to create your slideshow
                  </Typography>
                </Box>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="slides">
                    {(droppableProvided) => (
                      <Box
                        {...droppableProvided.droppableProps}
                        ref={droppableProvided.innerRef}
                      >
                        {slides.map((slide, index) => (
                          <Draggable
                            key={slide.id}
                            draggableId={slide.id!}
                            index={index}
                          >
                            {(draggableProvided) => (
                              <Paper
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                                sx={{
                                  p: 2,
                                  mb: 2,
                                  opacity: slide.published ? 1 : 0.7,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 2,
                                  }}
                                >
                                  <Box {...draggableProvided.dragHandleProps}>
                                    <DragHandleIcon color="action" />
                                  </Box>

                                  <Box sx={{ flex: 1 }}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 2,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color={
                                          slide.published
                                            ? 'success.main'
                                            : 'warning.main'
                                        }
                                      >
                                        {slide.published
                                          ? 'Published'
                                          : 'Draft'}
                                      </Typography>
                                    </Box>

                                    <TextField
                                      fullWidth
                                      label="Title"
                                      value={slide.title || ''}
                                      onChange={(e) =>
                                        handleSlideUpdate(
                                          index,
                                          'title',
                                          e.target.value,
                                        )
                                      }
                                      sx={{ mb: 2 }}
                                    />

                                    <TextField
                                      fullWidth
                                      label="Description"
                                      multiline
                                      rows={2}
                                      value={slide.description || ''}
                                      onChange={(e) =>
                                        handleSlideUpdate(
                                          index,
                                          'description',
                                          e.target.value,
                                        )
                                      }
                                      sx={{ mb: 2 }}
                                    />

                                    <Box
                                      sx={{ display: 'flex', gap: 2, mb: 2 }}
                                    >
                                      <TextField
                                        label="Start Time (seconds)"
                                        type="number"
                                        value={slide.startTime || 0}
                                        onChange={(e) =>
                                          handleSlideUpdate(
                                            index,
                                            'startTime',
                                            Number(e.target.value),
                                          )
                                        }
                                        sx={{ flex: 1 }}
                                      />
                                      <TextField
                                        label="End Time (seconds)"
                                        type="number"
                                        value={slide.endTime || 30}
                                        onChange={(e) =>
                                          handleSlideUpdate(
                                            index,
                                            'endTime',
                                            Number(e.target.value),
                                          )
                                        }
                                        sx={{ flex: 1 }}
                                      />
                                    </Box>

                                    <TextField
                                      fullWidth
                                      label="Button Text"
                                      value={slide.buttonText || ''}
                                      onChange={(e) =>
                                        handleSlideUpdate(
                                          index,
                                          'buttonText',
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </Box>

                                  <IconButton
                                    color="error"
                                    onClick={() => handleRemoveSlide(index)}
                                    disabled={saving}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              </Paper>
                            )}
                          </Draggable>
                        ))}
                        {droppableProvided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* Confirmation dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              confirmDialog.action()
              setConfirmDialog({ ...confirmDialog, open: false })
            }}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}
